"use client";

import { useSubmitForm } from "@/hooks/useSubmitForm";
import { Col, Form, Row } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { IoIosArrowDown } from "react-icons/io";
import PhoneInput from "react-phone-input-2";
import Button from "../Button/Button";
import "./ContactForm.scss";
import { useRouter } from "next/navigation";

const ContactForm = ({ title = "Get in Touch with Us" }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      phoneNumber: "+91",
      industryType: "",
    },
  });
  const router = useRouter();
  const { submitRequest, loading } = useSubmitForm();
  const industryType = watch("industryType");

  // const industryOptions = [
  //   "Manufacturing",
  //   "Retail",
  //   "Wholesale & Distribution",
  //   "Service / Maintenance",
  //   "Construction / Real Estate",
  //   "Logistics & Transportation",
  //   "Others",
  // ];

  const industryOptions = [
    "Manufacturing",
    "Information Technology & Services",
    "Healthcare & Pharmaceuticals",
    "Finance, Banking & Insurance",
    "Retail & E-Commerce",
    "Construction & Real Estate",
    "Automotive & Transportation",
    "Education & Training",
    "Telecommunications",
    "Energy & Utilities",
    "Professional Services & Consulting",
    "Other",
  ];

  const onSubmit = (data) => {
    submitRequest("/contact/", data, () => {
      reset();
      router.push("/thank-you");
    });
  };

  return (
    <div className="contact-form-container">
      <h3>{title}</h3>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-4">
              <Form.Label htmlFor="contact-name">
                Name<span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                id="contact-name"
                autoComplete="name"
                placeholder="Enter your name"
                {...register("name", {
                  required: "Name is required",
                  minLength: {
                    value: 2,
                    message: "Minimum 2 characters required",
                  },
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Name should contain only letters and spaces",
                  },
                  validate: (value) =>
                    value.trim().length >= 2 || "Name should not be empty",
                })}
              />
              {errors.name && (
                <p className="error-message">{errors.name.message}</p>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-4">
              <Form.Label htmlFor="contact-phone">
                Mobile Number <span className="required">*</span>
              </Form.Label>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: "Mobile number is required",
                  pattern: {
                    value: /^\+?[1-9][0-9]{9,14}$/,
                    message: "Enter valid phone number",
                  },
                }}
                render={({ field }) => (
                  <PhoneInput
                    country={"in"}
                    value={field.value}
                    onChange={(value, country, e, formattedValue) => {
                      const dialCode = `+${country.dialCode}`;

                      if (!value.startsWith(country.dialCode)) {
                        field.onChange(dialCode);
                        return;
                      }
                      if (!field.value?.startsWith(dialCode)) {
                        field.onChange(dialCode);
                      } else {
                        field.onChange(`+${value.replace(/\D/g, "")}`);
                      }
                    }}
                    onCountryChange={(country) => {
                      field.onChange(`+${country.dialCode}`);
                    }}
                    countryCodeEditable={false}
                    inputClass="phone-input"
                    containerClass="phone-input-container"
                    dropdownClass="phone-dropdown"
                    inputProps={{
                      id: "contact-phone",
                      name: "phoneNumber",
                      autoComplete: "tel",
                      required: true,
                      "aria-label": "Mobile Number",
                    }}
                  />
                )}
              />

              {/* <Form.Control
                                type="text"
                                placeholder="Enter your mobile number"
                                {...register("phoneNumber", {
                                    required: "Mobile number is required",
                                    pattern: {
                                        value: /^[0-9]{10}$/,
                                        message: "Enter valid 10-digit mobile number"
                                    }
                                })}
                                onInput={(e) => {
                                    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
                                }}
                            /> */}

              {errors.phoneNumber && (
                <p className="error-message">{errors.phoneNumber.message}</p>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-4">
              <Form.Label htmlFor="contact-email">
                Email Address <span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                id="contact-email"
                autoComplete="email"
                placeholder="Enter your email address"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="error-message">{errors.email.message}</p>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-4">
              <Form.Label htmlFor="contact-company">
                Company Name <span className="required">*</span>
              </Form.Label>
              <Form.Control
                id="contact-company"
                autoComplete="organization"
                type="text"
                placeholder="Enter your company name"
                {...register("company", {
                  required: "Company name is required",
                  minLength: {
                    value: 2,
                    message: "Minimum 2 characters required",
                  },
                })}
              />
              {errors.company && (
                <p className="error-message">{errors.company.message}</p>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group controlId="formIndustry" className="mb-4">
              <Form.Label>
                What is your Industry Type? <span className="required">*</span>
              </Form.Label>
              <div className="select-wrapper">
                <Form.Control
                  as="select"
                  className={`industry-select ${industryType ? "has-value" : "is-placeholder"}`}
                  {...register("industryType", {
                    required: "Industry type is required",
                  })}
                >
                  <option value="" disabled className="placeholder-option">
                    Select Your Industry Type
                  </option>

                  {industryOptions.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </Form.Control>

                <span className="select-icon">
                  <IoIosArrowDown />
                </span>
              </div>
              {errors.industryType && (
                <p className="error-message">{errors.industryType.message}</p>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group className="mb-4">
              <Form.Label htmlFor="contact-location">
                Location <span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                id="contact-location"
                autoComplete="address-level2"
                placeholder="Enter your company location"
                {...register("location", {
                  required: "Company location is required",
                  minLength: {
                    value: 2,
                    message: "Minimum 2 characters required",
                  },
                })}
              />
              {errors.location && (
                <p className="error-message">{errors.location.message}</p>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-4">
          <Form.Label htmlFor="contact-message">
            Message <span className="required">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            id="contact-message"
            rows={3}
            placeholder="Enter your message"
            className="no-resize"
            {...register("message", {
              required: "Message is required",
              minLength: {
                value: 10,
                message: "Minimum 10 characters required",
              },
              maxLength: {
                value: 5000,
                message: "Message cannot exceed 500 characters",
              },
            })}
          />
          {errors.message && (
            <p className="error-message">{errors.message.message}</p>
          )}
        </Form.Group>

        <Button
          variant="primary"
          size="lg"
          type="submit"
          disabled={loading}
          className="w-100 submit-btn"
        >
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </Form>
    </div>
  );
};

export default ContactForm;
